<template>
  <div class="portal">
    <!-- Sidebar -->
    <aside class="sidebar" :class="{ open: sidebarOpen }">
      <div class="sidebar-header">
        <img src="/assets/img/logo-trans.png" alt="Logo" class="sidebar-logo">
        <div class="logo-text">
          <span class="logo-space">Space Coast</span>
          <span class="logo-stem">STEM</span>

        </div>
        <button class="sidebar-toggle" @click="toggleSidebar">
          <span v-if="sidebarOpen">✕</span>
          <span v-else>☰</span>
        </button>
      </div>

      <nav class="sidebar-nav">
        <router-link
          to="/dashboard"
          class="nav-item"
          :class="{ active: currentTab === 'dashboard' }"
          @click="closeSidebarOnMobile"
        >
          <span class="nav-icon">📊</span>
          <span class="nav-label">Dashboard</span>
        </router-link>

        <router-link
          v-if="isAdminOrMaintainer"
          to="/admin"
          class="nav-item"
          :class="{ active: currentTab === 'admin' }"
          @click="closeSidebarOnMobile"
        >
          <span class="nav-icon">👥</span>
          <span class="nav-label">Admin</span>
          <span v-if="authStore.pendingUsersCount > 0" class="badge">{{ authStore.pendingUsersCount }}</span>
        </router-link>

        <router-link
          to="/requests"
          class="nav-item"
          :class="{ active: currentTab === 'requests' }"
          @click="closeSidebarOnMobile"
        >
          <span class="nav-icon">📋</span>
          <span class="nav-label">Requests</span>
          <span v-if="authStore.schoolRequestsCount > 0" class="badge">{{ authStore.schoolRequestsCount }}</span>
        </router-link>

        <router-link
          to="/inventory"
          class="nav-item"
          :class="{ active: currentTab === 'inventory' }"
          @click="closeSidebarOnMobile"
        >
          <span class="nav-icon">💻</span>
          <span class="nav-label">Inventory</span>
        </router-link>
      </nav>

      <div class="sidebar-footer">
        <div class="user-info">
          <div class="user-avatar">{{ userInitials }}</div>
          <div class="user-details">
            <div class="user-name">{{ authUser.username }}</div>
            <div class="user-role">
              {{ authStore.actualRole ? `Viewing as: ${authUser.role}` : authUser.role }}
            </div>
          </div>
          <button @click="openUserSettings" class="btn-settings" title="User Settings">
            <span>⚙️</span>
          </button>
        </div>

        <!-- Root Role Impersonation Selector -->
        <div v-if="isRootUser" class="role-impersonation">
          <label class="impersonation-label">View as:</label>
          <select
            v-model="selectedImpersonationRole"
            @change="handleRoleChange"
            class="role-selector"
          >
            <option :value="null">Root</option>
            <option value="admin">Admin</option>
            <option value="maintainer">Maintainer</option>
            <option value="user">User</option>
          </select>
        </div>

        <div class="theme-toggle">
          <span class="theme-label">Dark Mode</span>
          <label class="switch">
            <input type="checkbox" :checked="themeStore.isDark" @change="themeStore.toggleTheme">
            <span class="slider"></span>
          </label>
        </div>

        <button @click="handleLogout" class="btn-logout">
          <span>🚪</span> Logout
        </button>
      </div>
    </aside>

    <!-- Mobile overlay -->
    <div v-if="sidebarOpen" class="sidebar-overlay" @click="closeSidebar"></div>

    <!-- Main content -->
    <main class="portal-content">
      <router-view />
    </main>

    <!-- User Settings Modal -->
    <EditUserModal
      :show="showUserSettings"
      :user="currentUserForEdit"
      :schools="schools"
      :submitting="submittingUser"
      :error="userError"
      :can-edit-role="isAdmin"
      :is-self-edit="true"
      @close="closeUserSettings"
      @submit="updateCurrentUser"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore, assertIsLoggedIn } from '@/store/auth'
import { useThemeStore } from '@/store/theme'
import { api } from '@/composables/api'
import EditUserModal from '@/components/admin/EditUserModal.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const themeStore = useThemeStore()

const sidebarOpen = ref(false)
const showUserSettings = ref(false)
const currentUserForEdit = ref<any>(null)
const schools = ref<any[]>([])
const submittingUser = ref(false)
const userError = ref('')

const authUser = computed(() => authStore.authUser)
const selectedImpersonationRole = ref<string | null>(null)

// Check if user is actually root (for dropdown visibility)
const isRootUser = computed(() => {
  return (authStore.actualRole || authStore.authUser.role) === 'root'
})

// Permission checks now use authUser.role directly (which is overridden during impersonation)
const isAdmin = computed(() => {
  return authStore.authUser && authStore.authUser.isAdmin()
})
const isAdminOrMaintainer = computed(() => {
  return authStore.authUser && authStore.authUser.isAdminOrMaintainer()
})

const handleRoleChange = () => {
  authStore.setImpersonatedRole(selectedImpersonationRole.value)
}

const currentTab = computed(() => {
  const path = route.path
  if (path.includes('/dashboard')) return 'dashboard'
  if (path.includes('/admin')) return 'admin'
  if (path.includes('/requests')) return 'requests'
  if (path.includes('/inventory')) return 'inventory'
  return 'dashboard'
})

const userInitials = computed(() => {
  const name = authUser.value.username || 'U'
  return name.charAt(0).toUpperCase()
})

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}

const closeSidebar = () => {
  sidebarOpen.value = false
}

const closeSidebarOnMobile = () => {
  if (window.innerWidth < 768) {
    closeSidebar()
  }
}

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

// User settings functions
const loadSchools = async () => {
  try {
    const response = await api.get('/schools')
    schools.value = response.data || response || []
  } catch (error) {
    console.error('Error loading schools:', error)
    schools.value = []
  }
}

const openUserSettings = () => {
  userError.value = ''
  // Create a copy of the current user for editing
  currentUserForEdit.value = {
    ...authUser.value,
    _id: authUser.value._id || authUser.value.id,
    id: authUser.value._id || authUser.value.id
  }
  showUserSettings.value = true
}

const closeUserSettings = () => {
  showUserSettings.value = false
  userError.value = ''
  setTimeout(() => {
    currentUserForEdit.value = null
  }, 300)
}

const updateCurrentUser = async (userData: any) => {
  submittingUser.value = true
  userError.value = ''

  try {
    const userId = authUser.value._id || authUser.value.id
    await api.put(`/users/${userId}`, userData)

    // Refresh the current user data
    await authStore.fetchAuthUser()

    closeUserSettings()

    // Show success message (you could add a toast/snackbar here)
    console.log('User settings updated successfully!')
  } catch (error: any) {
    console.error('Error updating user:', error)
    if (error.response?.status === 403) {
      userError.value = 'You do not have permission to update this information.'
    } else if (error.response?.status === 409) {
      userError.value = 'A user with that username or email already exists.'
    } else {
      userError.value = error.response?.data?.message || 'Failed to update user settings. Please try again.'
    }
  } finally {
    submittingUser.value = false
  }
}

onMounted(async () => {
  // Initialize theme
  themeStore.initTheme()

  // Check authentication
  const isLoggedIn = await assertIsLoggedIn()
  if (!isLoggedIn) {
    router.push('/login')
  }

  // Load schools for user settings
  await loadSchools()

  // Fetch pending users count if admin or maintainer
  if (isAdminOrMaintainer.value) {
    await authStore.fetchPendingUsersCount()
  }

  // Fetch school requests count for badge
  await authStore.fetchSchoolRequestsCount()

  // Auto-open sidebar on desktop
  if (window.innerWidth >= 768) {
    sidebarOpen.value = true
  }
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.portal {
  display: flex;
  min-height: 100vh;
  background: var(--light-bg, #f5f7fa);
  position: relative;
}

/* Sidebar */
.sidebar {
  width: 260px;
  background: var(--white, #ffffff);
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  z-index: 1000;
  transition: transform 0.3s ease;
}

.sidebar-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
}

.sidebar-logo {
  width: 80px;
  height: 80px;
}

.logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  flex: 1;
}

.logo-space {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--white, #ffffff);
  letter-spacing: 0.5px;
  opacity: 0.95;
}

.logo-stem {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--white, #ffffff);
  letter-spacing: 2px;
  text-transform: uppercase;
}

.sidebar-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  flex: 1;
}

.sidebar-toggle {
  background: none;
  border: none;
  color: var(--white, #ffffff);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  display: none;
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  color: var(--text-dark, #333333);
  text-decoration: none;
  transition: all 0.3s;
  border-left: 3px solid transparent;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  position: relative;
}

.nav-item:hover {
  background: var(--light-bg, #f5f7fa);
  border-left-color: var(--secondary-color, #3ba3d1);
}

.nav-item.active {
  background: linear-gradient(90deg, rgba(59, 163, 209, 0.1) 0%, transparent 100%);
  border-left-color: var(--secondary-color, #3ba3d1);
  color: var(--secondary-color, #3ba3d1);
  font-weight: 600;
}

.nav-icon {
  font-size: 1.25rem;
}

.nav-label {
  font-size: 1rem;
  flex: 1;
}

.badge {
  background: #dc3545;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
  margin-left: auto;
}

/* Sidebar Footer */
.sidebar-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e0e0e0;
}

/* Role Impersonation */
.role-impersonation {
  margin-bottom: 1rem;
}

.impersonation-label {
  display: block;
  font-size: 0.8rem;
  color: var(--text-light, #666666);
  margin-bottom: 0.25rem;
}

.role-selector {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: var(--light-bg, #f5f7fa);
  color: var(--text-dark, #333333);
  font-size: 0.85rem;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
}

.role-selector:focus {
  outline: none;
  border-color: var(--secondary-color, #3ba3d1);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--secondary-color, #3ba3d1);
  color: var(--white, #ffffff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
}

.user-details {
  flex: 1;
}

.user-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text-dark, #333333);
}

.user-role {
  font-size: 0.8rem;
  color: var(--text-light, #666666);
  text-transform: capitalize;
}

.btn-settings {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  font-size: 1.2rem;
  opacity: 0.7;
  transition: all 0.3s;
  border-radius: 4px;
}

.btn-settings:hover {
  opacity: 1;
  background: var(--light-bg, #f5f7fa);
}

.btn-logout {
  width: 100%;
  padding: 0.75rem;
  background: var(--light-bg, #f5f7fa);
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: var(--text-dark, #333333);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: 'Inter', sans-serif;
}

.btn-logout:hover {
  background: #fee;
  border-color: #fcc;
  color: #c33;
}

/* Theme Toggle */
.theme-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  margin-bottom: 0.75rem;
}

.theme-label {
  font-size: 0.9rem;
  color: var(--text-dark, #333333);
  font-weight: 500;
}

/* Toggle Switch */
.switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: var(--secondary-color, #3ba3d1);
}

input:checked + .slider:before {
  transform: translateX(24px);
}

/* Main Content */
.portal-content {
  flex: 1;
  margin-left: 260px;
  padding: 2rem;
  transition: margin-left 0.3s ease;
}

/* Mobile Styles */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar.open {
    transform: translateX(0);
  }

  .sidebar-toggle {
    display: block;
  }

  .sidebar-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }

  .portal-content {
    margin-left: 0;
    padding: 1rem;
  }
}

/* Desktop - sidebar always visible */
@media (min-width: 769px) {
  .sidebar {
    transform: translateX(0);
  }

  .sidebar-overlay {
    display: none;
  }
}
</style>
